import React from 'react'
import * as VisuallyHidden from '@radix-ui/react-visually-hidden'

import { ColorThemeProps } from '@grupoboticario/flora'
import { keyframes, styled, FloraVariants, forwardRef } from '@grupoboticario/flora-react-system'

const rotate = keyframes({
  '0%': {
    transform: 'rotate(0deg)',
  },
  '100%': {
    transform: 'rotate(360deg)',
  },
})

const StyledSpinner = styled('span', {
  display: 'inline-block',
  position: 'relative',
  borderRadius: '$pill',
  transform: 'translateZ(0)',
  borderStyle: 'solid',

  '&:after': {
    borderRadius: '$pill',
  },

  variants: {
    size: {
      xxs: {
        size: '$2',
      },
      xs: {
        size: '$4',
      },
      sm: {
        size: '$6',
      },
      md: {
        size: '$8',
      },
      lg: {
        size: '$10',
      },
      xl: {
        size: '$12',
      },
    },
  },
  defaultVariants: {
    size: 'xs',
  },
})

export type SpinnerProps = FloraVariants<typeof StyledSpinner> & {
  /**
   * The thickness of the spinner
   * @example
   * ```jsx
   * <Spinner thickness="4px"/>
   * ```
   */
  thickness?: string
  /**
   * The speed of the spinner.
   * @example
   * ```jsx
   * <Spinner speed="0.2s"/>
   * ```
   */
  speed?: string
  /**
   * For accessibility, it is important to add a fallback loading text.
   * This text will be visible to screen readers.
   */
  label?: string
  /**
   * The color of the spinner
   */
  color?: ColorThemeProps
  /**
   * The color of the empty area in the spinner
   */
  emptyColor?: ColorThemeProps
}

/**
 * @component
 * @name Spinner
 * @description Spinner component
 * @tutorial https://flora.gb.tech/docs/components/core/spinner
 */
export const Spinner = forwardRef<'span', SpinnerProps>(
  (
    {
      thickness = '2px',
      speed = '0.8s',
      color = 'currentColor',
      emptyColor = 'transparent',
      label = 'Loading, please wait.',
      ...props
    },
    forwardedRef,
  ) => {
    const borderColor = /^\$/.test(color) ? `$colors${color}` : color
    const emptyBorderColor = /^\$/.test(emptyColor) ? `$colors${emptyColor}` : emptyColor

    return (
      <StyledSpinner
        data-flora-spinner
        data-flora="spinner"
        {...props}
        ref={forwardedRef}
        css={{
          ...(props.css as any),
          animation: `${rotate} ${speed} infinite linear`,
          borderColor,
          borderBottomColor: emptyBorderColor,
          borderLeftColor: emptyBorderColor,
          borderWidth: thickness,
        }}
      >
        <VisuallyHidden.Root>{props['aria-label'] || label}</VisuallyHidden.Root>
      </StyledSpinner>
    )
  },
)
